import React, { useMemo } from 'react';
import { withStyles, Icon, BodyText } from 'vopay';
import { useTranslation } from 'react-i18next';
import styles from './Complete.module.scss';
import { Title, DetailsRows } from '../../UI';

const classes = withStyles(styles);

interface DetailRow {
  label: React.ReactNode;
  value: React.ReactNode;
}

interface CompleteProps {
  title: string;
  amount?: string;
  username?: string;
  transactionType?: 'sent' | 'received';
  showAmounts?: boolean;
  details: DetailRow[];
  children?: React.ReactNode; // Optional additional content to render after details
  header?: React.ReactNode; // Optional header content to render above the title
}

const Complete: React.FC<CompleteProps> = ({
  title,
  amount,
  username,
  transactionType = 'sent',
  showAmounts = true,
  details,
  header,
  children,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'UI.Complete' });

  const transactionText = useMemo(() => {
    return transactionType === 'sent' ? t('youSent') : t('youReceived');
  }, [transactionType]);

  const recipientText = useMemo(() => {
    const message =
      transactionType === 'sent'
        ? t('to', {
            username: username || '',
          })
        : t('from', {
            username: username || '',
          });
    return message;
  }, [transactionType, username]);

  return (
    <div className={classes('container')}>
      {header && <div className={classes('header')}>{header}</div>}
      <div className={classes('content')}>
        {/* Success Icon */}
        <div className={classes('iconContainer')}>
          <div className={classes('successIcon')}>
            <Icon icon="check" height="34px" width="34px" color="primary-light" />
          </div>
          <Title level="3">{title}</Title>
        </div>

        {/* Success Message */}
        {showAmounts && (
          <div className={classes('messageContainer')}>
            <div className={classes('transactionType')}>{transactionText}</div>
            <Title level="1">{amount}</Title>
            <BodyText size="sm">{recipientText}</BodyText>
          </div>
        )}

        {/* Transaction Details */}
        <DetailsRows details={details} />

        {/* Additional Content */}
        {children}
      </div>
      {/* Footer */}
      <div className={classes('footer')}>
        <BodyText size="sm" weight="md" className={classes('poweredBy')}>
          {t('poweredBy')} <Icon icon="vLogoMark" height="20px" width="auto" color="primary-dark" />
        </BodyText>
      </div>
    </div>
  );
};

export default Complete;
